/// <amd-module name="Release/DeletedPipelines/InfoPage" />
import * as React from "react";

import { VssComponent, IVssLayoutManager, IWrappedComponentProperties, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { localeFormat } from "VSS/Core/Util/String";

import { Page } from "VSSUI/Page";
import { Header, TitleSize } from "VSSUI/Header";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { Image } from "VSSUI/Image";
import { ago, AgoFormat, duration } from "VSSUI/Utilities/Date";

import { StoreManager } from "DistributedTask/Flux/StoreManager";

import { IDeletedPipelinesListState, DeletedPipelinesStore } from "./Store";
import * as Resources from "Release/DeletedPipelines/Resources"
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { DeletedPipelinesActionCreator } from "./ActionCreator";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";

interface IPipelineDialogWithCommentProps<T> {
    dialogTitle: string;
    commentBoxTitle: string;
    onClose?: () => void;
    onOkClick: (comment: string) => Promise<T> | void;
    okDisabled?: boolean;
    footerInfoMessage?: string;
}

interface IInfoPageProps {
    onPipelineUndeleted?: (pipelineId: number, pipelineName: string) => void;
    onUndeleteFailed?: (pipelineId: number, pipelineName: string, error: any) => void,
    markTTI?: () => void;
}

export class InfoPage extends VssComponent<IInfoPageProps, IDeletedPipelinesListState> {
    constructor(props: IInfoPageProps) {
        super(props);

        this._actionCreator = ActionCreatorManager.GetActionCreator<DeletedPipelinesActionCreator>(DeletedPipelinesActionCreator);
        this._store = StoreManager.GetStore<DeletedPipelinesStore>(DeletedPipelinesStore);

        this.state = this._store.getState();
        this._store.addChangedListener(this._onUpdate);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onUpdate);
        TTIMarked = false;
    }

    public render(): React.ReactNode {
        const selectedPipeline = this.state.selectedPipeline
        let deletionDuration: string = "";

        if (selectedPipeline && selectedPipeline.deletedOn) {
            let dateToHardDelete = new Date(selectedPipeline.deletedOn.valueOf() + 30 * 24 * 60 * 60 * 1000 /* Pipeline is hard deleted after 30 days from soft deletion */);
            deletionDuration = duration(new Date(), dateToHardDelete);
        }

        return (
            selectedPipeline &&
            <Page
                className={"flex-grow"}
            >
                <Header
                    title={selectedPipeline.name}
                    titleSize={TitleSize.Large}
                    commandBarItems={[this._getRestoreCommand()]}
                />

                <TabBar
                    className={"release-right-tab-bar"}
                    selectedTabId={"recycle-bin"}
                    tabSize={TabSize.Tall}
                    onSelectedTabChanged={() => { }}
                >

                    <Tab
                        name={Resources.RecycleBinTabName}
                        id={"recycle-bin"}
                    />
                </TabBar>

                {selectedPipeline.deletedBy &&
                    <div className={"flex del-user-info"}>
                        <Image className={"flex-noshrink image"} src={getIdentityImageUrl(this.context.pageContext, selectedPipeline.deletedBy)} alt={selectedPipeline.deletedBy.displayName} />
                        <div className={"flex flex-column details"}>
                            <div className={"fontSizeL"}>{localeFormat(Resources.DeletedByFormat, selectedPipeline.deletedBy.displayName)}</div>
                            <div className={"deleted-on"} >{selectedPipeline.deletedOn && ago(selectedPipeline.deletedOn, AgoFormat.Extended)}</div>
                        </div>
                    </div>
                }

                {
                    deletionDuration &&
                    <div className={"del-pipe-retention-time"}>
                        {localeFormat(Resources.DeletionScheduledFormat, deletionDuration)}
                    </div>
                }

            </Page >);
    }

    public componentDidMount(): void {
        if (!this.state.isLoading && this.props.markTTI) {
            this.props.markTTI();
            TTIMarked = true;
        }
    }

    public componentDidUpdate(): void {
        if (!TTIMarked && !this.state.isLoading && this.props.markTTI) {
            this.props.markTTI();
            TTIMarked = true;
        }
    }

    private _onUpdate = () => {
        const state = this._store.getState();
        this.setState(state);
    };

    private _getRestoreCommand(): IHeaderCommandBarItem {
        return {
            id: "restore-pipeline-command",
            isPrimary: true,
            important: true,
            text: Resources.Restore,
            iconProps: { iconName: "Undo" },
            onActivate: () => {
                const id = (this.state.selectedPipeline && this.state.selectedPipeline.id) || -1;
                const name = (this.state.selectedPipeline && this.state.selectedPipeline.name) || "";
                if (id > 0) {

                    const dialogTitle = localeFormat(Resources.UndeleteDialogTitle, name);
                    _renderWrappedComponent(
                        this.context.pageContext,
                        dialogTitle,
                        (comment) => this._actionCreator.undeletePipeline(this.context.pageContext, id, name, comment, this.props.onPipelineUndeleted, this.props.onUndeleteFailed));
                }
            }
        };
    }

    private _store: DeletedPipelinesStore;
    private _actionCreator: DeletedPipelinesActionCreator;
}

export function getInfoPage(onPipelineUndeleted?: (pipelineId: number, pipelineName: string) => void, onUndeleteFailed?: (pipelineId: number, pipelineName: string, error: any) => void,
    markTTI?: () => void): React.ReactNode {
    return (
        <InfoPage
            onPipelineUndeleted={onPipelineUndeleted}
            onUndeleteFailed={onUndeleteFailed}
            markTTI={markTTI}
        />
    );
}

function _renderWrappedComponent(pageContext: IVssPageContext, dialogTitle: string, onOkClick: (comment: string) => void, children?: React.ReactNode[]): void {

    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

    layoutManager.renderCallout((onDismiss: () => void) => {
        const extendedProps: IWrappedComponentProperties<IPipelineDialogWithCommentProps<void>> & IPipelineDialogWithCommentProps<void> = {
            commentBoxTitle: Resources.Comment,
            dialogTitle: dialogTitle,
            onClose: onDismiss,
            onOkClick: onOkClick,
            dependencies: ["ms.vss-releaseManagement-web.release-command-dialogs"],
            wrappedType: "rel-pipe-comment-dialog",
        };
        return React.createElement(WrappedComponent, extendedProps, ...(children || []));
    });
}

let TTIMarked = false;

